// src/pages/Reports/IssuanceReporting/IssuanceStock/PrintHouseReceiptsTab.tsx
// RPT-003 (README.md mục 7) — nhập vé từ nhà in, 1 dòng = 1 phiếu nhập (PrintHouseDelivery).

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getPrintHouseReceiptReportApi } from 'helpers/fakebackend_helper';
import { ISSUANCE_REPORTS_BASE } from 'helpers/url_helper';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import PrintHouseSelect from '../shared/PrintHouseSelect';
import { cleanParams, formatDateTime, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { INITIAL_REPORT_PAGINATION, PrintHouseReceiptReportRow, ReportPaginationState } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const STATUS_OPTIONS = [
  { value: 'ChoNhan', label: st('cho_nhan_label') },
  { value: 'DaNhan', label: st('da_nhan_label') },
];

const PrintHouseReceiptsTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [printHouseId, setPrintHouseId] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<PrintHouseReceiptReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);

  const buildFilterParams = useCallback(
    () => ({
      IssuancePlanId: planId || undefined,
      PrintHouseId: printHouseId || undefined,
      Status: status || undefined,
    }),
    [planId, printHouseId, status],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPrintHouseReceiptReportApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_nhap_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, printHouseId, status]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(`${ISSUANCE_REPORTS_BASE}/PrintHouseReceipts`, buildFilterParams(), format, 'NhapVeTuNhaIn');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={3}>
          <Label className="form-label">{st('ky_phat_hanh_label')}</Label>
          <IssuancePlanSelect value={planId} onChange={(v) => { setPlanId(v); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('nha_in_label')}</Label>
          <PrintHouseSelect value={printHouseId} onChange={(v) => { setPrintHouseId(v); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('trang_thai_column')}</Label>
          <Input type="select" value={status} onChange={(e) => { setStatus(e.target.value); resetPage(); }}>
            <option value="">{st('tat_ca_trang_thai_option')}</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Input>
        </Col>
        <Col md={3} className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('ky_phat_hanh_label')}</th>
                <th>{st('nha_in_label')}</th>
                <th>{st('loai_ve_column')}</th>
                <th>{st('dai_so_column')}</th>
                <th className="text-end">{st('sl_chung_tu_column')}</th>
                <th className="text-end">{st('thuc_nhan_column')}</th>
                <th className="text-end">{st('thieu_column')}</th>
                <th className="text-end">{st('loi_column')}</th>
                <th className="text-end">{st('gia_tri_column')}</th>
                <th>{st('ngay_nhan_column')}</th>
                <th>{st('trang_thai_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={12} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.planCode}</td>
                  <td>{row.printHouseName}</td>
                  <td>{row.ticketTypeName || row.ticketType}</td>
                  <td>{row.rangeStart && row.rangeEnd ? `${row.rangeStart} - ${row.rangeEnd}` : '-'}</td>
                  <td className="text-end">{formatQty(row.expectedQuantity)}</td>
                  <td className="text-end">{formatQty(row.actualReceivedQuantity)}</td>
                  <td className="text-end text-danger">{formatQty(row.shortageQuantity)}</td>
                  <td className="text-end">{formatQty(row.errorCount)}</td>
                  <td className="text-end">{formatCurrency(row.value)}</td>
                  <td>{formatDateTime(row.receivedAt)}</td>
                  <td>
                    <span className={`badge bg-${row.status === 'DaNhan' ? 'success' : 'warning'}-subtle text-${row.status === 'DaNhan' ? 'success' : 'warning'}`}>
                      {row.statusName}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default PrintHouseReceiptsTab;
